import React from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";

import { ROUTES } from "@config/app.routing";

import { SERVICE_TYPE } from "@typings/globals";

import { buildUrl } from "@services/BuildUrl";
import { getQueryValue } from "@services/QueryParamsParser";

import { useAuth } from "@hooks/useAuth";
import { useFeatureToggle } from "@hooks/useFeatureToggle";

interface Props extends RouteComponentProps {
  serviceType: SERVICE_TYPE;
  children: React.ReactNode;
}

const ServiceTypeGuard = ({
  serviceType,
  ...props
}: Props): React.ReactElement => {
  const { isUserLoggedIn, isUserB2B } = useAuth();
  const isPreventMixingClientTypesEnabled = useFeatureToggle(
    "pwa_prevent_mixing_clients_b2b_with_b2c",
  );

  const isB2CClientWithB2BServiceType =
    !isUserB2B && serviceType === SERVICE_TYPE.B2B;
  const isB2BClientWithB2CServiceType =
    isUserB2B && serviceType !== SERVICE_TYPE.B2B;

  const redirectUrl = React.useMemo(() => {
    const postalCode = getQueryValue(
      props.history.location.search,
      "postalCode",
    );
    const url = buildUrl(ROUTES.WRONG_SERVICE_TYPE, {
      keyType: "colon",
    });

    url.setPath("serviceType", serviceType);
    url.setPath("postalCode", postalCode);

    return url.done();
  }, [props.history.location.search]);

  if (
    isUserLoggedIn &&
    isPreventMixingClientTypesEnabled &&
    (isB2CClientWithB2BServiceType || isB2BClientWithB2CServiceType)
  ) {
    return <Redirect to={redirectUrl} />;
  }

  return <React.Fragment>{props.children}</React.Fragment>;
};

export { ServiceTypeGuard };
